import { Box, Grid, Paper, Typography, useTheme } from '@mui/material';
import React, { useContext, useEffect, useState, useCallback } from "react";
import useStyles from "../../../style/js-style/containers/thirdparties/sheet/ThirdpartyInfosStyle.js"

import i18next from "../../../i18n/i18n";
import {
  formatDate,
  formatNumberCurrency,
  formatActivityText,
  getCountryName,
} from '../../../services/common/Utils';
import EAChip from "../../../components/EAChip";
import ThirdpartyIdentifiers from "../../../components/thirdparties/ThirdpartyIdentifiers";
import moment from "moment";
import { errorColor, missingColor, orangeColor } from "../../../style/globalStyle";
import EditIcon from "@mui/icons-material/Edit";
import EAFlag from "../../../components/EAFlag";
import EAIcon from "../../../components/common/EAIcon";
import { PrivilegeEnum, RoleBasedAccessControlContext } from "services/common/RolesUtils.js";
import UnmanagedThirdParty from '../../../components/thirdparties/unmanagedThirdParty/UnmanagedThirdParty';
import { unmanagedThirdPartyMode } from '../../../components/thirdparties/unmanagedThirdParty/UnmanagedThirdPartyUtils';
import { FormProvider, useForm } from 'react-hook-form';
import EAChipWithBackground from 'components/EAChipWithBackground.js';

const TpInfo = ({ title, children, beforeLogo = false }) => {
  const { classes } = useStyles();
  return (
    <>
      <Typography variant="h6" className={classes.blocTitle}>
        {beforeLogo !== false ? (
          <EAIcon icon={beforeLogo} className={classes.icon} />
        ) : ""}
        <span>{title}</span>
      </Typography>
      {children}
    </>
  );
};

const ThirdpartyInfosTitle = ({ thirdparty }) => {
  const { classes } = useStyles();
  return (
    <Grid container spacing={0} className={classes.thirdpartyTitleContainer}>

      {/*Header info : nationality, size etc. */}
      <Grid item xs={12} md={12}>
        <Box display="flex" alignItems="center" justifyContent="flex-start">

          {/* Country flag */}
          <EAFlag countryCode={thirdparty.addressCountryCode} customStyle={{ border: "4px solid white", borderRadius: "12px", position: "relative", left: "-12px"}}/>

          {/* collective proceedings */}
          {thirdparty.thirdpartyAlerts &&
            thirdparty.thirdpartyAlerts === "OUI" && (
              <EAChipWithBackground
                label={i18next.t("collectiveProceedingOngoing")}
                color={orangeColor}
              />
            )}
          {thirdparty.thirdpartyAlerts &&
            thirdparty.thirdpartyAlerts === "CLOTURÉE" && (
              <EAChipWithBackground
                label={i18next.t("collectiveProceedingClosed")}
                color={errorColor}
              />
            )}

            {thirdparty.hasRestrictedDisclosure &&  (
              <EAChipWithBackground
                label={i18next.t("hasRestrictedDisclosure.title")}
                color={missingColor}
                tooltip={i18next.t("hasRestrictedDisclosure.tooltip")}
              />
            )}

          {/* Headquarter*/}
          <EAChip label={thirdparty.headquarter ? i18next.t("headquarters") : i18next.t("secondaryBranch")} />

          {/* Catégorie */}
          {thirdparty.category && (
            <EAChip label={i18next.t(`misc.companyCategoryOptions.${thirdparty.category}`)} />
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

function ThirdpartyInfos({ thirdparty, isUnmanaged = false, fetchThirdpartyInfos = () => {}, thirdpartyCode, setIsUpdateThirdPartyDialogOpen,
                           onTpCodeChange }) {
  const { classes } = useStyles();
  const [identifiers, setIdentifiers] = useState();
  const { hasAnyOfPrivilegesOnScopedResourceImpl } = useContext(RoleBasedAccessControlContext);
  const [isUnmanagedEditionOpen, setIsUnmanagedEditionOpen] = React.useState(false);
  const { hasAnyOfPrivileges } = React.useContext(RoleBasedAccessControlContext);
  // const [isTPFusionEditionOpen, setIsTPFusionEditionOpen] = React.useState(false);
  const formMethods = useForm();
  const theme = useTheme();

  const locale = i18next.language || "en";
  moment.locale(locale);

  const hasAnyOfPrivilegesOnScopedResource = useCallback((...privileges) => {
    let result = false;

    if (thirdparty) {
      result = hasAnyOfPrivilegesOnScopedResourceImpl(thirdparty, ...privileges);
    }

    return result;
  }, [thirdparty, hasAnyOfPrivilegesOnScopedResourceImpl]);

  const getAddress = () => {
    return <>
      {' '}
      {thirdparty.addressStreet || ""}
      {" " + (thirdparty.additionalAddressDetails || "")}
      {", " + (thirdparty.addressPostCode || "")} {thirdparty.addressCity || ""}
      {", " + getCountryName(thirdparty.addressCountryCode, i18next.language) || ""}
      {' '}
    </>
  }

  // Get Third party Identifiers
  useEffect(() => {
    const res = [];

    if (hasAnyOfPrivilegesOnScopedResource(PrivilegeEnum.PRIVILEGE_EDIT_THIRD_PARTY_CODE)) {
      res.push({
        type: i18next.t("tpCode"),
        value: thirdpartyCode || "",
        icon: <EditIcon fontSize="small" />,
      });
    } else {
      res.push({
        type: i18next.t("tpCode"),
        value: thirdpartyCode || "",
      });
    }

    if (thirdparty.duns && !thirdparty.dunsEncrypted) {
      res.push({ type: "DUNS", value: thirdparty.duns });
    }

    if (!isUnmanaged){
      res.push({ type: "Siren", value: thirdparty.siren });
      res.push({ type: "Siret", value: thirdparty.siret });
    } else {
      res.push({ type: i18next.t('unmanagedThirdParty.label.id_local'), value: thirdparty.companyIdValue });
    }

    res.push({ type: i18next.t("VAT"), value: thirdparty.tva });
    if (thirdparty.addressCountryCode?.toUpperCase() !== "FR") {
      thirdparty.listRegistrationNumber?.forEach((regNumber) => {
        res.push({ type: regNumber.libelle, value: regNumber.number });
      });
    }

    if(thirdparty.leiStatus){
      res.push({ type: "LEI", value: thirdparty.lei, lei_status: thirdparty.leiStatus });
    }

    setIdentifiers(res);
  }, [thirdparty, thirdpartyCode, hasAnyOfPrivilegesOnScopedResource, isUnmanaged, setIsUpdateThirdPartyDialogOpen]);

  return (
    <>
      <Paper elevation={0} className={classes.root}>
        <Grid container spacing={0}>
          <Grid item xs={12}>
            <ThirdpartyInfosTitle thirdparty={thirdparty} />
          </Grid>
          <Grid container item xs={12} spacing={2}>

            {/* Informations */}
            <Grid item xs={6} md={6}>
              <TpInfo title={i18next.t("information")} beforeLogo="information">
                {thirdparty.companyStartDate &&
                    <div className={classes.typoLineWrapper}>
                      <Typography className={classes.typoLabel}>
                        {' '}
                        {i18next.t("creationDate")} :{' '}
                      </Typography>
                      <Typography className={classes.typoContent}>
                        {' '}
                        {thirdparty.companyStartDate !== false ? (
                          <span>
                            {formatDate(thirdparty.companyStartDate)} (
                            {moment(thirdparty.companyStartDate).fromNow(true)})
                          </span>
                        ) : (
                          ''
                        )}{' '}
                      </Typography>
                      <br />
                    </div>
                }
                {thirdparty.legalCategoryLabel &&
                    <div className={classes.typoLineWrapper}>
                      <Typography className={classes.typoLabel}>
                        {' '}
                        {i18next.t("legalCategory")} :{' '}
                      </Typography>
                      <Typography className={classes.typoContent}>
                        {' '}
                        {thirdparty.legalCategoryLabel || ''}{' '}
                      </Typography>
                      <br />
                    </div>
                }
                  <div className={classes.typoLineWrapper}>
                    <Typography className={classes.typoLabel}>
                      {' '}
                      {i18next.t("capital")} :{' '}
                    </Typography>
                    <Typography className={classes.typoContent}>
                      {' '}
                      <span>
                        {formatNumberCurrency(
                          thirdparty.capital,
                          thirdparty.capitalCurrency
                        )}
                      </span>{' '}
                    </Typography>
                    <br />
                  </div>
                  <div className={classes.typoLineWrapper}>
                    <Typography className={classes.typoLabel}>
                      {' '}
                      {i18next.t("activity")} :{' '}
                    </Typography>
                    <Typography className={classes.typoContent}>
                      {' '}
                      {formatActivityText(
                        thirdparty.etabActivityType,
                        thirdparty.etabActivityCode,
                        thirdparty.etabActivityLabel
                      )}{' '}
                    </Typography>
                    <br />
                  </div>
                {thirdparty.subCategory && (
                    <div className={classes.typoLineWrapper}>
                      <Typography className={classes.typoLabel}>
                        {' '}
                        {i18next.t("thirdpartySheet.sub-category.label")} :{' '}
                      </Typography>
                      <Typography className={classes.typoContent}>
                        {' '}
                        {i18next.t(
                          `thirdpartySheet.sub-category.${thirdparty.subCategory}`
                        )}{' '}
                      </Typography>
                      <br />
                    </div>
                )}
                {thirdparty.brandName && (
                  <div className={classes.typoLineWrapper}>
                    <Typography className={classes.typoLabel}>
                      {' '}
                      {i18next.t("thirdpartySheet.brandName")} :{' '}
                    </Typography>
                    <Typography className={classes.typoContent}>
                      {' '}
                      {thirdparty.brandName}{' '}
                    </Typography>
                    <br />
                  </div>
                )}

                {/* Adresse */}
                  <div className={classes.typoLineWrapper} style={{ marginTop: "16px" }}>
                    <Typography className={classes.typoLabel}>
                      {' '}
                      <EAIcon icon={"coordinate"}/>
                      {i18next.t("thirdpartySheet.address")} :{' '}
                    </Typography>
                    <Typography className={classes.typoContent} style={{color: theme.palette.primary.primary500}}>
                      {getAddress()}
                    </Typography>
                    <br />
                  </div>
              </TpInfo>
            </Grid>

            {/* Identifiers */}
            <Grid item xs={6} md={6}>
              <TpInfo title={i18next.t("identifiers")} beforeLogo="identifier">
                <ThirdpartyIdentifiers
                  identifiers={identifiers}
                  size="full"
                  hasAnyOfPrivilegesOnScopedResource={hasAnyOfPrivilegesOnScopedResource}
                  thirdpartyCode={thirdpartyCode}
                  onTpCodeChange={onTpCodeChange}
                  tpId={thirdparty?.thirdpartyId}
                />
              </TpInfo>
            </Grid>
          </Grid>
        </Grid>

        {/* Tiers non géré */}
        {isUnmanaged && (
          <div className={classes.unmanagedThirdPartyInfoContainer}>
            <EAIcon
              icon={'unmanagedThirdParty/unmanagedTP'}
              style={{ marginRight: '8px' }}
            />
            <span>
              {i18next.t('unmanagedThirdParty.ThisThirdPartyIsNotImmat')}
            </span>
            {hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_THIRD_PARTY_UNMANAGED) && (
              <span
                className={classes.unmanagedThirdPartyEditProfil}
                onClick={() => setIsUnmanagedEditionOpen(true)}
              >
                {i18next.t('unmanagedThirdParty.editProfil')}
              </span>
            )}
          </div>
        )}
        {isUnmanaged && isUnmanagedEditionOpen && (
        <FormProvider {...formMethods}>
          <UnmanagedThirdParty
            isOpen={true}
            onClose={() => {
              setIsUnmanagedEditionOpen(false);
              fetchThirdpartyInfos()
            }}
            mode={unmanagedThirdPartyMode.EDIT}
            thirdparty={thirdparty}
          />
        </FormProvider>
        )}

        {/*/!* TODO Tiers doublon *!/*/}
        {/*{isThirdPartyAlreadyExist && (*/}
        {/*  <div className={classes.unmanagedThirdPartyInfoContainer}>*/}
        {/*    <EAIcon icon={'thirdparty_green'} className={classes.iconTP} />*/}
        {/*    <span>*/}
        {/*      {i18next.t('unmanagedThirdParty.thirdPartyAlreadyExist')}*/}
        {/*    </span>*/}
        {/*    <span*/}
        {/*      className={classes.consultTP}*/}
        {/*      onClick={() => console.log("TODO redirection page TP")}*/}
        {/*    >*/}
        {/*      {i18next.t('unmanagedThirdParty.consult')}*/}
        {/*    </span>*/}
        {/*    <span*/}
        {/*      className={classes.unmanagedThirdPartyEditProfil}*/}
        {/*      onClick={() => setIsTPFusionEditionOpen(true)}*/}
        {/*    >*/}
        {/*      {i18next.t('unmanagedThirdParty.fusionTP')}*/}
        {/*    </span>*/}
        {/*  </div>*/}
        {/*)}*/}
        {/*{isThirdPartyAlreadyExist && isTPFusionEditionOpen && (<></>)}*/}
      </Paper>
    </>
  );
}

export default ThirdpartyInfos;
